import { API_ROUTES_FOR_AXIOS } from '../constants/ApiRoutes';
import { axiosInstance } from '../constants/axiosInstance';
import { ApiResponse, PaginatedResponse } from 'src/types/commonResponseTypes';
import {
  ConnectorApiResponse,
  ConnectConnectorBody,
  DisconnectConnectorBody,
  ConnectedConnectorWithPopulatedConfig,
  McpAuthStatus,
  McpOauthProviderPreset,
  McpOauthStartPayload,
  McpOauthStartResponse,
  McpToolCatalog,
} from 'src/types/connector';
import { CONNECTOR_NAME, CONNECTOR_VARIANT } from 'src/constants/connectors';
import { handleError } from 'src/utils/commonErrorUtils';

export const getConnectedConnectorsInOrg = async (
  variant?: CONNECTOR_VARIANT,
): Promise<ApiResponse<ConnectorApiResponse[]>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['getConnectedConnectorsInOrg'](variant);
    const { data } = await axiosInstance.get(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const getConnectedConnectorsWithPopulatedConfigInOrgByType = async (
  connectorType: CONNECTOR_NAME,
  page = 1,
  limit = 10,
  searchQuery?: string,
  variant?: CONNECTOR_VARIANT,
): Promise<
  ApiResponse<PaginatedResponse<ConnectedConnectorWithPopulatedConfig[]>>
> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['getConnectedConnectorsByType'](
      connectorType,
      page,
      limit,
      searchQuery,
      variant,
    );
    const { data } = await axiosInstance.get(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const connectConnector = async (
  body: ConnectConnectorBody,
): Promise<ApiResponse<ConnectorApiResponse[]>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['connectConnector']();
    const { data } = await axiosInstance.post(apiUrl, body);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const disconnectConnector = async (
  body: DisconnectConnectorBody,
): Promise<ApiResponse<string>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['disconnectConnector']();
    const { data } = await axiosInstance.post(apiUrl, body);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const generateUniqueApiKey = async (): Promise<ApiResponse<string>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['generateUniqueApiKey']();
    const { data } = await axiosInstance.get(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const getConnectorById = async (
  connectorId: string,
): Promise<ApiResponse<ConnectorApiResponse>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['getConnectorById'](connectorId);
    const { data } = await axiosInstance.get(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const getAsanaConnectorSignInUrl = async (): Promise<
  ApiResponse<string>
> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['getAsanaConnectorSignInUrl']();
    const { data } = await axiosInstance.get(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const discoverMcpTools = async (
  connectedConnectorId: string,
): Promise<ApiResponse<McpToolCatalog>> => {
  try {
    const apiUrl =
      API_ROUTES_FOR_AXIOS['discoverMcpTools'](connectedConnectorId);
    const { data } = await axiosInstance.post(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const getMcpTools = async (
  connectedConnectorId: string,
): Promise<ApiResponse<McpToolCatalog>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['getMcpTools'](connectedConnectorId);
    const { data } = await axiosInstance.get(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const refreshMcpAuth = async (
  connectedConnectorId: string,
): Promise<ApiResponse<{ refreshed: boolean }>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['refreshMcpAuth'](connectedConnectorId);
    const { data } = await axiosInstance.post(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const getMcpOauthProviders = async (): Promise<
  ApiResponse<McpOauthProviderPreset[]>
> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['getMcpOauthProviders']();
    const { data } = await axiosInstance.get(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const startMcpOauth = async (
  body: McpOauthStartPayload,
): Promise<ApiResponse<McpOauthStartResponse>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['startMcpOauth']();
    const { data } = await axiosInstance.post(apiUrl, body);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const getMcpAuthStatus = async (
  connectedConnectorId: string,
): Promise<ApiResponse<McpAuthStatus>> => {
  try {
    const apiUrl =
      API_ROUTES_FOR_AXIOS['getMcpAuthStatus'](connectedConnectorId);
    const { data } = await axiosInstance.get(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};
